import { Link, useNavigate } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import { Box } from 'designSystem/components/Box'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from 'designSystem/components/Sidebar'
import { SidebarCollapseToggle } from 'designSystem/components/SidebarCollapseToggle'
import { ChatbotSidePanel } from 'features/chatbot'
import {
  NotificationPreferencesModal,
  NotificationsDropdownSidebar,
} from 'features/forecastNotifications'
import { DocumentsBugReportButton } from 'features/knowledgeDocumentViewer/components/bugReport/DocumentsBugReportButton'
import type { ViewerBugReportContext } from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { VIEWER_BUG_REPORT_ALLOWED_ROLES } from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { ConfigSettingsSidebar } from 'features/navbar/configSettings/ConfigSettingsSidebar'
import { NAV_ITEMS_CONFIG, NavItemConfig } from 'features/navbar/navConfig'
import { ProfileAndSettingsSidebar } from 'features/navbar/profileAndSettings/ProfileAndSettingsSidebar'
import { SiteSelectorsSidebar } from 'features/navbar/siteSelectors/SiteSelectorsSidebar'
import { SelectedTab } from 'features/navbar/types'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useNavbarData } from 'features/navbar/useNavbarData'
import { useSelectedOrgAndSiteInterface } from 'features/orgAndSiteSourceOfTruth'
import { useCanVisitSiteView } from 'features/overview'
import { PermissionsGate, useGeckoPermissions } from 'features/permissions'
import { useWorkspaceTabs } from 'features/workspaceTabs/WorkspaceTabsContext'
import React, { useCallback, useState } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import styled from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { EmbeddedSidebarNavItem } from './EmbeddedSidebarNavItem'

interface EmbeddedSidebarProps {
  siteID: string | undefined
  orgID: string | undefined
  orgOrSiteID: string | undefined
}

export const EmbeddedSidebar: React.FC<EmbeddedSidebarProps> = ({
  siteID,
  orgID,
  orgOrSiteID,
}) => {
  const navigate = useNavigate()
  const [isNotificationPreferencesOpen, setIsNotificationPreferencesOpen] =
    useState(false)
  const [isChatbotOpen, setIsChatbotOpen] = useState(false)

  const { allowedNavbarItems } = useNavbarConfig({
    orgId: orgID ?? '',
    siteId: siteID,
  })
  const { site, sites, organization, organizations, isLoading, hasEverLoaded } =
    useNavbarData(orgOrSiteID)

  const { setOrgAndSiteIds } = useSelectedOrgAndSiteInterface()
  const { openDestinationTab } = useWorkspaceTabs()
  const { hasPermissions, user } = useGeckoPermissions()
  const canVisitSiteView = useCanVisitSiteView()
  const isInternalUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const isExternalAudience = !isInternalUser

  const onNavigateItemSelected = useCallback(
    async (
      event: React.MouseEvent<HTMLAnchorElement>,
      destinationKey: string,
      label: string
    ) => {
      event.preventDefault()
      event.stopPropagation()
      const destinationUrl = new URL(event.currentTarget.href)
      openDestinationTab({
        destinationKey,
        label,
        href: `${destinationUrl.pathname}${destinationUrl.search}${destinationUrl.hash}`,
      })
      await setOrgAndSiteIds({
        organizationId: orgID,
        siteId: siteID,
      })
    },
    [openDestinationTab, orgID, siteID, setOrgAndSiteIds]
  )

  const onFooterWorkspaceNavigate = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      info: { label: string; destinationKey: string }
    ) => {
      void onNavigateItemSelected(event, info.destinationKey, info.label)
    },
    [onNavigateItemSelected]
  )

  const handleOpenUpload = useCallback(() => {
    navigate({
      to: '/documents/upload',
      search: { [SEARCH_PARAM_SITE_ID]: site?.id },
    })
  }, [navigate, site?.id])

  const buildSidebarBugReportContext = useCallback(
    (action: string): ViewerBugReportContext => ({
      reportedByEmail: user?.email,
      siteId: site?.id ?? '',
      recentActions: [action],
    }),
    [site?.id, user?.email]
  )

  const navItems = (
    Object.entries(NAV_ITEMS_CONFIG) as Array<[SelectedTab, NavItemConfig]>
  )
    .filter(([tabKey]) => {
      if (!allowedNavbarItems) return true
      return allowedNavbarItems.includes(tabKey)
    })
    .filter(([, config]) => {
      if (config.shouldShow) {
        return config.shouldShow({ siteID: siteID ?? '', canVisitSiteView })
      }
      return true
    })

  return (
    <Sidebar collapsible='icon' variant='embedded'>
      <SidebarHeader>
        <SidebarSiteSelectorsShell>
          <SiteSelectorsSidebar
            selectedOrganization={organization}
            organizations={organizations}
            selectedSite={site}
            sites={sites}
            isLoading={isLoading}
            hasEverLoaded={hasEverLoaded}
          />
        </SidebarSiteSelectorsShell>
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map(([tabKey, config]) => {
              const menuItem = (
                <EmbeddedSidebarNavItem
                  key={tabKey}
                  config={config}
                  siteID={siteID}
                  orgID={orgID}
                  onClick={(event) =>
                    onNavigateItemSelected(event, tabKey, config.label)
                  }
                />
              )

              if (config.permissions) {
                return (
                  <PermissionsGate
                    key={tabKey}
                    requires={config.permissions}
                    except={config.except}>
                    {menuItem}
                  </PermissionsGate>
                )
              }

              return menuItem
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <PermissionsGate requires={VIEWER_BUG_REPORT_ALLOWED_ROLES}>
            <DocumentsBugReportButton
              buildContext={buildSidebarBugReportContext}
              openActionLabel='Opened bug report from embedded sidebar'
              renderTrigger={({ disabled, onClick }) => (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    type='button'
                    disabled={disabled || !site?.id}
                    variant='embedded'
                    tooltip='Report Issue'
                    aria-label='Report Issue'
                    onClick={onClick}>
                    <LucideIcon name='Bug' />
                    <span>Report Issue</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            />
          </PermissionsGate>
          <SidebarMenuItem>
            <SidebarMenuButton
              type='button'
              isActive={isChatbotOpen}
              variant='embedded'
              tooltip='Ask Cantilever'
              aria-label={
                isChatbotOpen ? 'Close AI Assistant' : 'Open AI Assistant'
              }
              aria-expanded={isChatbotOpen}
              onClick={() => setIsChatbotOpen((isOpen) => !isOpen)}>
              <LucideIcon name='Sparkle' />
              <span>Ask Cantilever</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {!isExternalAudience && (
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <SidebarMenuItem>
                <SidebarMenuButton asChild variant='embedded'>
                  <Link
                    to='/entry-browser'
                    title='Entry Browser'
                    onClick={(event) =>
                      onNavigateItemSelected(
                        event,
                        SelectedTab.EntryBrowser,
                        'Entry Browser'
                      )
                    }>
                    <LucideIcon name='Database' />
                    <span>Entry Browser</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </PermissionsGate>
          )}
          {!isExternalAudience && (
            <ConfigSettingsSidebar
              orgID={organization?.id}
              siteID={site?.id}
              onWorkspaceNavigate={onFooterWorkspaceNavigate}
            />
          )}
          {!isExternalAudience && <NotificationsDropdownSidebar />}
          <ProfileAndSettingsSidebar
            onOpenNotificationPreferences={() =>
              setIsNotificationPreferencesOpen(true)
            }
            onOpenUploadDocuments={site?.id ? handleOpenUpload : undefined}
            showNotificationPreferences={!isExternalAudience}
          />
        </SidebarMenu>
      </SidebarFooter>
      <SidebarSeparator />
      <SidebarFooter>
        <EmbeddedSidebarFooterBar />
      </SidebarFooter>
      <NotificationPreferencesModal
        isOpen={isNotificationPreferencesOpen}
        onClose={() => setIsNotificationPreferencesOpen(false)}
      />
      <ChatbotSidePanel
        isOpen={isChatbotOpen}
        onClose={() => setIsChatbotOpen(false)}
      />
    </Sidebar>
  )
}

const EmbeddedSidebarFooterBar: React.FC = () => {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  return (
    <Box
      display='flex'
      align='center'
      justify={isCollapsed ? 'center' : undefined}
      paddingTop='sm'>
      {!isCollapsed ? (
        <SidebarFlavorText aria-hidden='true'>
          <SidebarFlavorTextLine>Cantilever</SidebarFlavorTextLine>
          <SidebarFlavorTextLine>Gecko Robotics</SidebarFlavorTextLine>
        </SidebarFlavorText>
      ) : null}
      <SidebarCollapseToggle />
    </Box>
  )
}

const SidebarSiteSelectorsShell = styled.div`
  width: 100%;
  min-width: 0;
`

const SidebarFlavorText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  padding-inline: 0.5rem;
  font-family: 'Roboto Mono', 'SF Mono', 'Menlo', monospace;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.12em;
  line-height: 1.25;
  text-transform: uppercase;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const SidebarFlavorTextLine = styled.span`
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
`
